import React, { useContext, useEffect, useRef } from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";
import { getUserErrors } from "common-client/utils/apollo";
import { useDebouncedCallback } from "common-client/utils/useDebouncedCallback";
import { useExecuteSearchLazyQuery } from "../../generated/graphql";
import { useOutsideAlerter } from "../../utils/outsideAlerter";
import { FlexColumn } from "../Common/__styles__/Layout";
import Divider from "../Common/Divider";
import { Icon } from "../Common/Icons/LucideIcons";
import { Text } from "../Inputs";
import { InternalMapContext } from "../Maps/InternalMapContextProvider";
import { Bounds } from "../Maps/utils/viewportHook";
import {
  BackgroundPanelOverlay,
  LeftContentWrapper,
  OmniboxPanelContainer,
  OmniboxSearchContainer,
  OmniboxSearchInputWrapper,
} from "./__styles__/OmniboxSearch";
import {
  getSearchErrorFromMessage,
  SEARCH_ERROR,
} from "./hooks/useSearchResultsState";
import { OmniboxRecentSearches } from "./OmniboxRecentSearches";
import { OmniboxResults } from "./OmniboxResults";
import { SearchContext } from "./SearchContext";

const OmniboxSearchInputField = ({
  outline,
  value,
  formControlOnChange,
}: {
  outline: boolean;
  value: string;
  formControlOnChange?: (value: string) => void;
}) => {
  const {
    accountId,
    searchQueryState,
    searchQueryStateDispatch,
    searchResultsDispatch,
    handleSearchClose,
    calculateBounds,
  } = useContext(SearchContext);

  const [executeSearch] = useExecuteSearchLazyQuery({
    fetchPolicy: "network-only",
    onCompleted: ({ search: { results, bounds } }) => {
      searchResultsDispatch({
        type: "searchComplete",
        results,
        bounds: (bounds ?? null) as Bounds | null,
      });
    },
    onError: error => {
      const userError = getUserErrors(error);
      searchResultsDispatch({
        type: "searchError",
        error:
          userError ?
            getSearchErrorFromMessage(userError)
          : SEARCH_ERROR.UNKNOWN,
      });
    },
  });

  const debouncedExecuteSearch = useDebouncedCallback(async (val: string) => {
    const trimmedVal = val.trim();
    searchResultsDispatch({ type: "searchStarted", query: trimmedVal });
    if (trimmedVal.length >= 3) {
      await executeSearch({
        variables: {
          query: trimmedVal,
          options: { accountId, calculateBounds },
        },
      });
    }
  }, 250);

  const handleChange = async (inputValue: string) => {
    formControlOnChange?.(inputValue);
    if (inputValue) {
      searchQueryStateDispatch({ type: "searchEntered", query: inputValue });
      await debouncedExecuteSearch(inputValue);
    } else {
      searchQueryStateDispatch({ type: "searchCleared" });
      searchResultsDispatch({ type: "resetSearch" });
    }
  };

  const handleClose = () => {
    handleSearchClose();
    formControlOnChange?.("");
  };

  return (
    <OmniboxSearchInputWrapper outline={outline}>
      <LeftContentWrapper>
        <Icon iconName="search" color="contentSecondary" size="16" />
        <Text
          name="omnibox-search-input"
          data-testid="omnibox-search-input"
          placeholder="Search..."
          onChange={handleChange}
          onFocus={() =>
            searchQueryStateDispatch({
              type: "setSearchPanel",
              searchPanelOpen: true,
            })
          }
          size="small"
          value={value}
          maxLength={256}
          style={{
            padding: 0,
            border: "none",
            height: "24px",
          }}
        />
      </LeftContentWrapper>
      {(value || searchQueryState.searchPanelOpen) && (
        <Icon
          iconName="x"
          color="contentSecondary"
          size="16"
          data-testid="omnibox-search-close"
          style={{ cursor: "pointer" }}
          onClick={handleClose}
        />
      )}
    </OmniboxSearchInputWrapper>
  );
};

const OmniboxSearchInput = <T extends FieldValues>({
  outline,
  ...formControlProps
}: {
  outline: boolean;
} & FormControlProps<T>) => {
  const { searchQueryState } = useContext(SearchContext);

  if (formControlProps.control) {
    return (
      <Controller
        {...formControlProps}
        render={({ field }) => (
          <OmniboxSearchInputField
            outline={outline}
            value={field.value ?? ""}
            formControlOnChange={field.onChange}
          />
        )}
      />
    );
  }

  return (
    <OmniboxSearchInputField
      outline={outline}
      value={searchQueryState.query ?? ""}
    />
  );
};

export type FormControlProps<T extends FieldValues> =
  | {
      control: Control<T>;
      name: Path<T>;
      rules?: RegisterOptions<T, Path<T>>;
    }
  | {
      control?: never;
      name?: never;
      rules?: never;
    };

type OmniboxSearchProps<T extends FieldValues> = {
  backgroundPanel?: boolean;
  compactSearchPanel?: boolean;
  outline?: boolean;
  overlaySearchPanel?: boolean;
} & FormControlProps<T>;

export const OmniboxSearch = <T extends FieldValues>({
  backgroundPanel = false,
  compactSearchPanel = false,
  outline = false,
  overlaySearchPanel = false,
  ...formControlProps
}: OmniboxSearchProps<T>) => {
  const {
    searchQueryState,
    searchQueryStateDispatch,
    resetSearch,
    enableRecentSearches,
  } = useContext(SearchContext);

  // needed while we still have the old search around
  const { setResetSearchCategory } = useContext(InternalMapContext);
  useEffect(() => {
    if (setResetSearchCategory) {
      setResetSearchCategory(() => {
        resetSearch();
      });
    }
  }, [setResetSearchCategory, resetSearch]);

  const searchFieldDirty =
    formControlProps.control ?
      formControlProps.control.getFieldState(formControlProps.name).isDirty
    : true;

  const backgroundPanelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  useOutsideAlerter({
    ref: [searchRef, backgroundPanelRef],
    onOutsideInteraction: () => {
      if (searchQueryState.searchPanelOpen && !searchQueryState.query) {
        searchQueryStateDispatch({
          type: "setSearchPanel",
          searchPanelOpen: false,
        });
      }
    },
    dependencies: [searchQueryState.searchPanelOpen],
  });

  return (
    <>
      {backgroundPanel && searchQueryState.searchPanelOpen && (
        <BackgroundPanelOverlay ref={backgroundPanelRef} />
      )}
      <OmniboxSearchContainer ref={searchRef}>
        <FlexColumn>
          <OmniboxSearchInput outline={outline} {...formControlProps} />
          {backgroundPanel && searchQueryState.searchPanelOpen && (
            <Divider extension={16} />
          )}
        </FlexColumn>
        {searchQueryState.searchPanelOpen &&
          searchQueryState.query &&
          searchFieldDirty && (
            <OmniboxPanelContainer
              outline={outline}
              compact={compactSearchPanel}
              overlay={overlaySearchPanel}
            >
              <OmniboxResults expandPlaceholders={backgroundPanel} />
            </OmniboxPanelContainer>
          )}
        {searchQueryState.searchPanelOpen &&
          !searchQueryState.query &&
          enableRecentSearches && (
            <OmniboxPanelContainer
              outline={outline}
              compact={compactSearchPanel}
              overlay={overlaySearchPanel}
            >
              <OmniboxRecentSearches expandPlaceholders={backgroundPanel} />
            </OmniboxPanelContainer>
          )}
      </OmniboxSearchContainer>
    </>
  );
};
